import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { modalSecondaryActionClass, secondaryActionDisabledClass } from '../utils/homeStyles';

const dangerActionClass = 'home-rounded inline-flex w-full items-center justify-center rounded-2xl border border-[#9c5656] bg-[#8b4d4d] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_3px_10px_rgba(85,45,45,0.22)] transition-all duration-150 hover:bg-[#784242] hover:shadow-[0_5px_14px_rgba(85,45,45,0.28)] sm:text-base';

/**
 * Reusable confirmation modal for destructive actions
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Called when user cancels or closes modal
 * @param {Function} props.onConfirm - Called when user confirms the action
 * @param {string} props.title - Modal title
 * @param {string} props.description - Modal description/body text
 * @param {string} [props.confirmLabel='Resetten'] - Label for confirm button
 * @param {string} [props.cancelLabel='Annuleren'] - Label for cancel button
 * @param {boolean} [props.isConfirming=false] - Whether the confirm action is in progress
 */
export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Resetten',
  cancelLabel = 'Annuleren',
  isConfirming = false
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#1f2530]/55 backdrop-blur-[2px] dark:bg-black/60"
        onClick={onClose}
        aria-hidden="true"
        data-testid="confirmation-backdrop"
      />

      {/* Modal content */}
      <div className="home-rounded relative z-10 w-full max-w-2xl rounded-[28px] border border-[#d2d8e2] bg-[#f1f2f5] shadow-[0_24px_60px_rgba(40,49,62,0.35)] dark:border-secondary-700 dark:bg-secondary-900">
        <div className="p-6 sm:p-7">
          {/* Header */}
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#f0d8d8] text-[#7a2f2f] dark:bg-rose-900/30 dark:text-rose-300">
                <AlertTriangle size={20} />
              </span>
              <div>
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-[#9f6e6e] dark:text-rose-300/80">
                  Let op
                </p>
                <h2 id="confirmation-modal-title" className="m-0 text-2xl font-extrabold text-[#4e5563] dark:text-secondary-100">
                  {title}
                </h2>
              </div>
            </div>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#c2ccd9] bg-white text-[#5d6675] transition-colors hover:bg-[#edf1f7] hover:text-[#3f4958] disabled:cursor-not-allowed disabled:opacity-60 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-300 dark:hover:bg-secondary-700 dark:hover:text-secondary-100"
              onClick={onClose}
              aria-label="Dialoog sluiten"
              disabled={isConfirming}
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-[#d2d8e2] bg-[#f1f2f5] p-4 dark:border-secondary-700 dark:bg-secondary-900">
              <p className="m-0 text-sm leading-relaxed text-[#5d6675] dark:text-secondary-300">
                {description}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              className={`${dangerActionClass} ${isConfirming ? 'cursor-not-allowed opacity-60 hover:bg-[#8b4d4d] hover:shadow-[0_3px_10px_rgba(85,45,45,0.22)]' : ''}`}
              onClick={onConfirm}
              disabled={isConfirming}
            >
              {isConfirming ? 'Bezig...' : confirmLabel}
            </button>
            <button
              type="button"
              className={`${modalSecondaryActionClass} ${isConfirming ? secondaryActionDisabledClass : ''}`}
              onClick={onClose}
              disabled={isConfirming}
            >
              {cancelLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
